import React, { useState } from 'react'
import AsyncSelect from 'react-select/async'
import _ from 'lodash'

import * as Constants from '../../helpers/constants'
import { ApiHelper } from '../../helpers/apihelper'
import * as Helper from '../../helpers/helper'

const entityIconClass = ({ user }) => 'fa-solid ' + (user.user_type === 'organization' ? 'fa-users' : 'fa-user')

// Same USER_LIST call shape as the header search (header/components/user_search) and the
// AsyncSelect precedent in coupons/create_coupon.js -- {value, label} per react-select's contract,
// with the raw user attached so formatOptionLabel/onChange don't have to re-derive it.
const loadUserOptions = ({ client, query }) => {
  if (!query || !query.trim()) return Promise.resolve([])

  const apiConfig = ApiHelper(client, 'USER_LIST')
  const body = _.pick({
    skip: 0,
    limit: 10,
    search: query.trim(),
    sort: { created_on: 'desc' },
    filters: {},
    client_secret: Constants.CLIENT_SECRET,
  }, apiConfig.API_BODY)

  return apiConfig.API(client, body)
    .then((response) => {
      if (response && response.status === Constants.CO_API.SUCCESS) {
        return response.data.data.map((user) => ({ value: user._id, label: user.email, user }))
      }
      return []
    })
    .catch(() => [])
}

// Both the open dropdown list and the collapsed selected value get the same user-vs-org icon and
// right-aligned paid badge -- the collapsed value stays single-line ("Name — email"), the dropdown
// row stacks name/email so the row reads well at the row's own width.
const formatOptionLabel = (option, { context }) => {
  const { user } = option
  const paidBadge = Helper.getPaidTag(user) && <span className='dpfu-entity-search-paid-badge'>Paid</span>

  if (context === 'value') {
    return (
      <span className='dpfu-entity-search-value'>
        <i className={entityIconClass({ user }) + ' dpfu-entity-search-value-icon'} />
        <span className='dpfu-entity-search-value-text'>
          {(user.name || '—') + ' — ' + (user.email || 'No email provided')}
        </span>
        {paidBadge}
      </span>
    )
  }

  return (
    <div className='dpfu-entity-search-result'>
      <i className={entityIconClass({ user }) + ' dpfu-entity-search-result-icon'} />
      <span className='dpfu-entity-search-result-text'>
        <span className='dpfu-entity-search-result-name'>{user.name || '—'}</span>
        <span className='dpfu-entity-search-result-email'>{_.truncate(user.email, { length: 40 }) || 'No email provided'}</span>
      </span>
      {paidBadge}
    </div>
  )
}

/* Type-ahead user/org picker for the concurrency-limit editor, built on react-select's AsyncSelect
 * (already the app's own pattern for a searched user picker -- see coupons/create_coupon.js)
 * instead of a hand-rolled input+dropdown -- no custom positioning, no outside-click handling, no
 * portal, all of that is react-select's own responsibility. */
const EntitySearch = ({ client, onSelect }) => {
  const [selectedOption, setSelectedOption] = useState(null)

  const onChange = (option) => {
    setSelectedOption(option)
    onSelect(option ? option.user : null)
  }

  return (
    <AsyncSelect
      cacheOptions
      loadOptions={(query) => loadUserOptions({ client, query })}
      onChange={onChange}
      value={selectedOption}
      formatOptionLabel={formatOptionLabel}
      noOptionsMessage={() => 'No results found'}
      loadingMessage={() => 'Searching…'}
      isClearable
      placeholder='Search user by name, email, or ID'
      className='dpfu-entity-search-container'
      classNamePrefix='dpfu-entity-search'
    />
  )
}

export default EntitySearch
